import React, { RefObject, memo, useState, useEffect, useContext } from 'react'
import { useMutation } from '@apollo/client/react'
import { towingServiceTypeOptions } from '../../../common/options'
import { FormatMessageType, LockMessage, LockedForName, TowingOrderType, TypeIdentifier } from '../../../common/types'
import { ALOrderSubForm } from '../../../components/ALOrderSubForm'
import { Button } from '../../../components/Button'
import { LahitapiolaDefaultOrderDetailsForm } from '../../../components/LahitapiolaSubForm'
import {
  Column,
  FormPadder,
  FormSubGroup,
  FormSubGroupHeader,
  FormSubGroupHeading,
  FormSubGroupWrapper,
  FormSubGroupsList,
  Row,
  Section,
  SectionColumn,
  SimpleRow,
  ButtonColumn,
  ButtonRow,
} from '../../../components/layout'
import { ScrollToError } from '../../../components/layout/form/ScrollToError'
import { FormikForm as Form } from '../../../components/layout/form/style'
import { Modal } from '../../../components/Modal'
import { useHistoryPush } from '../../../components/router'
import { SosServiceOrderDetailsForm } from '../../../components/SosSubForm/TowingOrderForm/EditTowingOrder'
import { VehicleInfo } from '../../../components/VehicleSearchModal/types'
import { persistentDelete } from '../../../util/localStorage/localStorage'
import { translateInputOptions } from '../../../util/translateInputOptions'
import { CustomerFormFields } from '../../Customer/CustomerFormFields'
import { initialOrderer } from '../../Customer/CustomerFormFields/customerToOrderer'
import { EditCustomer } from '../../Customer/EditCustomer'
import { CustomerFormValues } from '../../Customer/types'
import { calculateStationCount } from '../../Parameters/station'
import { StateStore } from '../../StoreProvider'
import { HeadingColumn, IconColumn } from '../../TowingRecordPage/EditTowingRecord/EditTowingRecordPage'
import { OwnersHoldersTrafi } from '../../../components/VehicleDetails/types'
import { TowingOrderRoutesForm } from '../AddTowingOrder/TowingOrderRoutesForm'
import { TowingOrderSaveButtons } from '../components/SaveButtons'
import { TowingOrderDetailsSubFormContents } from './TowingOrderDetailsSubFormContents'
import { TowingOrderSettingsForm } from './TowingOrderSettingsForm'
import { EditTowingOrder } from './types'
import { getStoredCompanyId } from '../../../graphql-client/storedState'
import { useSocket } from '../../../util/useSocket'
import { WarningBanner } from '../../../components/TowingCommon/WarningBanner'
import { VehicleInfoFormFields } from '../../../components/VehicleDetails/VehicleInfoFormFields'
import { RESET_TOWING_ORDER_VEHICLE_INFO_CHECK, useResetVehicleInfoCheck } from '../../../components/VehicleSearchModal/query'
import { setFatalErrorNotification, setErrorNotifications } from '../../../components/responses/setErrorNotifications'
import { setSuccessNotification } from '../../../components/notification'
import { CenteredLoader } from '../../../components/responses/Loading'
import { useSettings } from '../../Parameters'
import { TowingRecordItems } from '../../../components/TowingRecordItems'
import { PhoneInput, SelectInput, TextInput } from '../../../components/layout/form/input'
import { populatePrepaymentByDebtorType } from '../utils/populatePrepaymentByDebtorType'
import { isPrepaymentAlreadyActive, isPrepaymentLinkSent } from '../../../components/TowingCommon/getPrepaymentLabel'
import {
  CANCEL_TOWING_ORDER_PAYMENT_LINK,
  RESEND_TOWING_ORDER_PAYMENT_LINK,
  TowingOrderPaymentLinkMutationVariables,
} from './paymentLinkMutations'

const debtorOptions = [
  { value: 'orderer', label: 'Työn tilaaja' },
  { value: 'holder', label: 'Ajoneuvon haltija' },
  { value: 'owner', label: 'Ajoneuvon omistaja' },
  { value: 'other', label: 'Muu' },
]

const convertModalMessage = `Täydennä puuttuvat tiedot. Kun kaikki tiedot on täytetty ja painat tallenna, niin tilaus muunnetaan SOS tilaukseksi.`

type PendingAction = 'resend' | 'cancel' | null

const confirmationConfig = {
  resend: {
    label: 'Lähetä uudelleen',
    message: 'Haluatko uudelleenlähettää maksulinkin asiakkaalle?',
    confirmLabel: 'Lähetä uudelleen',
    confirmButtonCategory: 'save' as const,
    successTitle: 'Maksulinkki lähetetty uudelleen',
    successMessage: 'Maksulinkki on lähetetty uudelleen asiakkaalle.',
  },
  cancel: {
    label: 'Poista maksulinkki',
    message: 'Peruuttaminen avaa maksurivit uudelleen muokattavaksi. Haluatko peruuttaa maksulinkin?',
    confirmLabel: 'Poista maksulinkki',
    confirmButtonCategory: 'danger' as const,
    successTitle: 'Maksulinkki peruutettu',
    successMessage: 'Maksulinkki on peruutettu ja maksurivit ovat jälleen muokattavissa.',
  },
}

interface FormContentsProps {
  formatMessage: FormatMessageType
  onCancelVehicleDetails: () => void
  onHandleVehicleResults: (res: VehicleInfo) => void
  onSetOrderer: (customer: CustomerFormValues) => void
  closeForm: () => void
  showTowingDateSpan: boolean
  orderer: CustomerFormValues
  ssn: string
  registrationNumber: string
  setFieldValue: (field: string, value: any) => void
  operatorDisabled: boolean
  setStoreFormValues: (storeValues: boolean) => void
  typeIdentifier: TypeIdentifier
  isSos?: boolean
  onSubmit: any
  getValue: any
  // onEditTime?: (delayedTowing: boolean) => void SIIRRETTY SISEMPÄÄN KOMPONENTTIIN
  convertToSosOrder: () => void
  scrollToSos?: boolean
  businessId: string
  towingOrder: EditTowingOrder
  ownersHolders: OwnersHoldersTrafi[]
  setOrdererFromTrafi: (customer: CustomerFormValues) => void
  // onChangeVisibility: (value: string | number | undefined) => void
  scrollTo: string | undefined
}

export const TowingOrderFormContentsMemo: React.FunctionComponent<FormContentsProps> = ({
  formatMessage,
  onCancelVehicleDetails,
  onHandleVehicleResults,
  onSetOrderer,
  showTowingDateSpan,
  orderer,
  ssn,
  registrationNumber,
  setFieldValue,
  operatorDisabled,
  setStoreFormValues,
  onSubmit,
  typeIdentifier,
  isSos = false,
  getValue,
  convertToSosOrder,
  scrollToSos,
  businessId,
  towingOrder,
  ownersHolders,
  setOrdererFromTrafi,
  scrollTo
}) => {
  const [showCustomerEditForm, setShowCustomerEditForm] = useState<boolean>(false)
  const [showConvertToSosModal, setShowConvertToSosModal] = useState<boolean>(false)
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [isLocked, setIsLocked] = useState<LockedForName>(null)
  const { state } = useContext(StateStore)
  const settings = useSettings()
  const companyId = getStoredCompanyId()
  const historyPush = useHistoryPush()
  const socket = useSocket()
  const atjRef: RefObject<any> = React.createRef()

  const prepaymentStatus = getValue('prepaymentStatus')
  const prepaymentType = getValue('prepaymentType')
  const prepaymentLinkSent = prepaymentType === 'online_payment' && isPrepaymentLinkSent(prepaymentStatus)
  const prepaymentFieldsLocked = prepaymentType === 'online_payment' && isPrepaymentAlreadyActive(prepaymentType, prepaymentStatus)
  const showPrepaymentDetails = prepaymentType === 'online_payment' || prepaymentLinkSent

  const mutationOptions = (typename: string, action: 'resend' | 'cancel') => ({
    onCompleted(result: any) {
      const mutationName = action === 'resend' ? 'resendTowingOrderPaymentLink' : 'cancelTowingOrderPaymentLink'
      const payload = result[mutationName]
      if (payload.__typename === typename) {
        const cfg = confirmationConfig[action]
        setSuccessNotification(cfg.successTitle, cfg.successMessage)
        historyPush(`/towing-order/${towingOrder.id}`)
      } else {
        setErrorNotifications({ data: payload })
      }
    },
    onError(err: any) {
      setFatalErrorNotification(err)
    },
  })

  const [doResend, { loading: resendLoading }] = useMutation<any, TowingOrderPaymentLinkMutationVariables>(
    RESEND_TOWING_ORDER_PAYMENT_LINK,
    mutationOptions('ResendTowingOrderPaymentLinkSuccess', 'resend'),
  )
  const [doCancel, { loading: cancelLoading }] = useMutation<any, TowingOrderPaymentLinkMutationVariables>(
    CANCEL_TOWING_ORDER_PAYMENT_LINK,
    mutationOptions('CancelTowingOrderPaymentLinkSuccess', 'cancel'),
  )

  const mutationLoading = resendLoading || cancelLoading

  const handleConfirmPaymentLinkAction = () => {
    if (!towingOrder.id || !pendingAction) return
    const input = { towingOrderId: towingOrder.id }
    if (pendingAction === 'resend') doResend({ variables: { input } })
    else if (pendingAction === 'cancel') doCancel({ variables: { input } })
    setPendingAction(null)
  }

  const [resetVehicleInfo, { loading }] = useResetVehicleInfoCheck(RESET_TOWING_ORDER_VEHICLE_INFO_CHECK, {
    onCompleted() {
      //const result = data.resetTowingOrderVehicleInfoCheck
      onCancelVehicleDetails()
      //setValue('lastModified', result.lastModified)
    },
    onError(err) {
      setFatalErrorNotification(err)
    }
  }) 

  useEffect(() => {
    if (scrollTo === 'atj' && atjRef.current) {
      atjRef.current.scrollIntoView()
    }
  }, [atjRef, scrollTo])

  useEffect(() => {
    if (!companyId) {
      return
    }
    const callback = (event: string) => {
      const data: LockMessage = JSON.parse(event)
      setIsLocked(data.userName ? data.userName : null)
    }
    socket.on(`company-${companyId}.towing_order-${towingOrder.id}.locked`, callback)
    socket.on(`company-${companyId}.towing_order-${towingOrder.id}.unlocked`, callback)
    return () => {
      console.log('removing event listeners from towing order')
      socket.removeEventListener(`company-${companyId}.towing_order-${towingOrder.id}.locked`, callback)
      socket.removeEventListener(`company-${companyId}.towing_order-${towingOrder.id}.unlocked`, callback)
    }
  }, [companyId, socket, towingOrder])

  if (!settings) {
    return null
  }

  const paymentLinkEnabled = settings.companyData?.paymentLinkEnabled ?? false

  const prepaymentTypeOptions = [
    { label: 'Ei ennakkomaksua', value: '' },
    {
      label: paymentLinkEnabled
        ? 'Maksulinkki (Ennakkomaksu)'
        : 'Maksulinkki (Ennakkomaksu) (ota yhteys asiakaspalveluun)',
      value: 'online_payment',
      isDisabled: !paymentLinkEnabled,
    },
  ]

  const operatorId = getValue('operatorId')
  const userId = state.currentUser ? state.currentUser.user_id : null
  const userRole = state.currentUser ? state.currentUser.role : null
  const orderType = isSos ? TowingOrderType.sos : TowingOrderType.default
  const stationRequired = settings ? calculateStationCount(settings.stations) > 0 : false
  const {
    modules: { sos: canConvertToSos },
    notifications,
  } = settings

  const sosRef: RefObject<any> = React.createRef()

  const handleResetVehicleInfoCheck = async () => {
    const towingOrderId = getValue('id')
    const vehicleInfoCheckId = getValue('vehicleInfoCheckId')

    if(towingOrderId && vehicleInfoCheckId) {
      resetVehicleInfo({variables: { input: {towingOrderId: towingOrderId} }})
      return 
    }

    onCancelVehicleDetails()
  }
 
  return (
    <>
      {loading && <CenteredLoader />}
      {isLocked && <WarningBanner title={'Työ lukittu'} message={`${isLocked} on avannut työn muokattavaksi.`} />}
      {showCustomerEditForm && (
        <EditCustomer
          orderer={orderer}
          onClose={() => setShowCustomerEditForm(false)}
          onUpdate={updatedOrderer => {
            onSetOrderer(updatedOrderer)
            setShowCustomerEditForm(false)
          }}
        />
      )}
      {showConvertToSosModal && (
        <Modal
          label="Muunna SOS tilaukseksi"
          message={convertModalMessage}
          close={() => setShowConvertToSosModal(false)}
          onConfirm={() => convertToSosOrder()}
          confirmLabel="Muunna SOS tilaukseksi"
          closeLabel="Peruuta"
        />
      )}
      {pendingAction && (
        <Modal
          label={confirmationConfig[pendingAction].label}
          message={confirmationConfig[pendingAction].message}
          close={() => setPendingAction(null)}
          onConfirm={handleConfirmPaymentLinkAction}
          confirmLabel={confirmationConfig[pendingAction].confirmLabel}
          confirmButtonCategory={confirmationConfig[pendingAction].confirmButtonCategory}
          closeLabel="Peruuta"
        />
      )}

      <Form>
        {/* {showConvertToSosModal && (
          <ConvertToSosOrderModal onConvert={convertToSosOrder} onClose={() => setShowConvertToSosModal(false)} />
        )} */}
        <SimpleRow margin="0.5rem 1rem 0.5rem 0.5rem" mobileMargin="0.5rem 0.5rem 0.5rem 0" justify="space-between">
          <IconColumn
            direction="back"
            label="Takaisin"
            onClick={() => {
              setStoreFormValues(false)
              persistentDelete('towingOrder')
              historyPush(`/towing-jobs`)
            }}
          />
          {canConvertToSos &&
            typeIdentifier !== TypeIdentifier.autoliitto &&
            typeIdentifier !== TypeIdentifier.lahitapiola &&
            !isSos && (
              <HeadingColumn>
                <Button category="save" label={'Muunna SOS työksi'} onClick={() => setShowConvertToSosModal(true)} />
              </HeadingColumn>
            )}
        </SimpleRow>
        <FormSubGroupsList>
          <TowingOrderSettingsForm
            operatorDisabled={operatorDisabled}
            isAl={typeIdentifier === TypeIdentifier.autoliitto}
            stationRequired={stationRequired}
            notificationSettings={notifications}
            isSos={isSos}
            userRole={userRole}
          />

          <VehicleInfoFormFields
            formatMessage={formatMessage}
            onHandleVehicleResults={onHandleVehicleResults}
            onCancelVehicleDetails={handleResetVehicleInfoCheck}
            registrationNumber={registrationNumber}
            getValue={getValue}
            setValue={setFieldValue}
          />

          <CustomerFormFields
            editCustomer={() => setShowCustomerEditForm(true)}
            orderer={orderer}
            clearOrderer={() => {
              onSetOrderer(initialOrderer)
            }}
            onUpdate={updatedOrderer => onSetOrderer(updatedOrderer)}
            ssn={ssn}
            setFieldValue={setFieldValue}
            getValue={getValue}
            isSos={isSos}
            businessId={businessId}
            ownersHolders={ownersHolders}
            setOrdererFromTrafi={setOrdererFromTrafi}
          />
          {typeIdentifier === TypeIdentifier.autoliitto && (
            <ALOrderSubForm
              serviceTypeOptions={translateInputOptions(towingServiceTypeOptions, formatMessage)}
              setFieldValue={setFieldValue}
              getOriginalValue={getValue}
              towingOrder={towingOrder}
            />
          )}
          {isSos && typeIdentifier === TypeIdentifier.sos && (
            <SosServiceOrderDetailsForm
              getValue={getValue}
              setValue={setFieldValue}
              sosRef={sosRef}
              scrollToThis={scrollToSos}
            />
          )}
          {typeIdentifier === TypeIdentifier.lahitapiola && <LahitapiolaDefaultOrderDetailsForm />}
          {!isSos && typeIdentifier !== TypeIdentifier.autoliitto && typeIdentifier !== TypeIdentifier.lahitapiola && (
            <TowingOrderDetailsSubFormContents formatMessage={formatMessage} showTowingDateSpan={showTowingDateSpan} />
          )}
          <TowingOrderRoutesForm
            setValue={setFieldValue}
            getValue={getValue}
            orderType={orderType}
            deliverableName="sosServiceOrder.deliverable"
          />
          <FormSubGroupWrapper>
            <TowingRecordItems
              jobItems={getValue('orderItems') ?? []}
              discountItem={getValue('discountItem')}
              itemsIncludeVat={getValue('itemsIncludeVat') ?? false}
              fieldArrayName="orderItems"
              setValue={setFieldValue}
              disabled={prepaymentFieldsLocked}
            />
          </FormSubGroupWrapper>
          <FormSubGroupWrapper>
            <FormSubGroupHeader>
              <Row>
                <Column>
                  <FormSubGroupHeading>Ennakkomaksu</FormSubGroupHeading>
                </Column>
              </Row>
            </FormSubGroupHeader>
            <FormSubGroup>
              <Section columns={2}>
                <SectionColumn>
                  <SelectInput
                    label="Maksutapa"
                    name="prepaymentType"
                    options={prepaymentTypeOptions}
                    clearable
                    disabled={prepaymentFieldsLocked}
                  />
                </SectionColumn>
              </Section>
              {showPrepaymentDetails && (
                <Section columns={2}>
                  <SectionColumn>
                    <SelectInput
                      label="Maksun suorittaja"
                      name="prepaymentDebtorType"
                      options={debtorOptions}
                      disabled={prepaymentFieldsLocked}
                      onChange={(selection) => {
                        if (selection && typeof selection !== 'number') {
                          populatePrepaymentByDebtorType(getValue, setFieldValue, selection)
                        }
                      }}
                    />
                  </SectionColumn>
                  <SectionColumn>
                    <TextInput
                      label="Maksajan nimi"
                      name="prepaymentPaymentDetails.debtorName"
                      disabled={prepaymentFieldsLocked}
                    />
                  </SectionColumn>
                  <SectionColumn>
                    <TextInput
                      label="Sähköpostiosoite kuittia varten"
                      name="prepaymentPaymentDetails.receiptEmail"
                      type="email"
                      disabled={prepaymentFieldsLocked}
                    />
                  </SectionColumn>
                  <SectionColumn>
                    <PhoneInput
                      label="Puhelinnumero"
                      name="prepaymentPaymentDetails.debtorPhone"
                      required
                      disabled={prepaymentFieldsLocked}
                    />
                  </SectionColumn>
                </Section>
              )}
              {prepaymentLinkSent && (
                <Section>
                  <SectionColumn>
                    <ButtonRow padding="0" justify="start">
                      <ButtonColumn>
                        <Button
                          category="save"
                          label="Lähetä uudelleen"
                          onClick={() => setPendingAction('resend')}
                          disabled={mutationLoading}
                        />
                      </ButtonColumn>
                      <ButtonColumn>
                        <Button
                          category="danger"
                          label="Poista maksulinkki"
                          onClick={() => setPendingAction('cancel')}
                          disabled={mutationLoading}
                        />
                      </ButtonColumn>
                    </ButtonRow>
                  </SectionColumn>
                </Section>
              )}
            </FormSubGroup>
          </FormSubGroupWrapper>
        </FormSubGroupsList>
        <FormPadder>
          <TowingOrderSaveButtons userId={userId} userRole={userRole} operatorId={operatorId} handleSubmit={onSubmit} />
        </FormPadder>
        <ScrollToError />
      </Form>
    </>
  )
}

export const TowingOrderFormContents = memo(TowingOrderFormContentsMemo)
